// tslint:disable: no-inferrable-types

import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  effect,
  signal,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, Subject, of } from 'rxjs';

import {COMMA, ENTER} from '@angular/cdk/keycodes';

import { EasFormControl, EasFormGroup } from './easform';
import { EasFieldOptions } from './easform.interfaces';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
    selector: 'eas-easform-control-multiselect',
    templateUrl: './easform-control-multiselect.component.html',
    styles: [
        `
      :host {
        display: flex;
        width: 100%;
      }

      :host > * {
        flex: 1 1 auto;
      }
    `
    ],
    standalone: false
})
export class EasFormControlMultiSelectComponent implements OnDestroy, OnInit, OnChanges {
  @ViewChild('input') input!: ElementRef<HTMLInputElement>;

  @Input() easControl!: EasFormControl;
  @Input() errors!: string[];

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly selectedCategories = signal<any[]>([]);
  readonly initialValueLoaded = signal<boolean>(false);


  private unsubscribe: Subject<void> = new Subject();
  public $isLoading = signal<boolean>(false);
  private inputsValueChanges: Subject<string> = new Subject();
  public errorMsg: string | undefined;
  public currentErrors: string[] = []

  private allCategories: any[] = [];

  private _categories$ = new BehaviorSubject<any[]>([]);
  categories$ = this._categories$.asObservable().pipe(distinctUntilChanged());

  constructor(

  ) {
    effect(() => {
      if (this.initialValueLoaded()) {
        this.easControl.control.setValue(this.selectedCategories().map(item => item.id));
      }
    })
  }

  private setupSimpleCategories() {
    const options = this.easControl.field.options || {};
    this.allCategories = options.categories || [];
    this._categories$.next(this.allCategories);
    this.inputsValueChanges.pipe(
      startWith(''),
      takeUntil(this.unsubscribe),
      map((value) => {
        const name = this.displayFn()(value);
        const sense = name || value;
        const filtered = this._filter(sense);
        this._categories$.next(filtered);
      })
    );
  }
 ngOnChanges(changes: SimpleChanges): void {
    if (changes['easControl'] && this.easControl) {
      this.handleErrors();
    }
  }
  handleErrors() {
    this.currentErrors = [];
    if (this.easControl.control.errors) {
      for (const key in this.easControl.control.errors) {
        this.currentErrors = this.errors[key](key);
      }
    }
  }

  private setupFactoryCategories() {
    const options = this.easControl.field.options || ({} as EasFieldOptions);
    if (options.categoriesFactory === undefined) {
      return;
    }

    const root = this.easControl.root;
    const categories = options.categoriesFactory(
      this.easControl,
      this.easControl.control.value,
      this.easControl.parent?.parent?.control?.value,
      root.control?.value
    );

    this.allCategories = categories;
    this._categories$.next(categories);

    // We can have a new set of categories when the whole form changes
    // This is quite expensive but hopefully noone is using it
    if (root.control) {
      root.control.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
        if (options.categoriesFactory === undefined) {
          return;
        }
        this.allCategories = options.categoriesFactory(
          this.easControl,
          this.easControl.control.value,
          this.easControl?.parent?.parent?.control?.value,
          value
        );
        this._categories$.next(this.allCategories);
      });
    }

    this.inputsValueChanges.pipe(
      startWith(''),
      takeUntil(this.unsubscribe),
      map((value) => {
        const name = this.displayFn()(value);
        const sense = name || value;
        const filtered = this._filter(sense);
        this._categories$.next(filtered);
      })
    );
  }

  filter() {
    this.inputsValueChanges.next(this.input.nativeElement.value);
  }

  private setupSourceCategories() {
    const options = this.easControl.field.options || ({} as EasFieldOptions);
    if (this.input === undefined) {
      throw new Error('An input is undefined. Cannot proceed with initialisation');
    }

    const setup = () => {
      this.inputsValueChanges
        .pipe(
          map(() => {
            if (this.input === undefined) {
              return '';
            }
            return this.input.nativeElement.value;
          }),
          filter((res) => {
            return res !== null && res.length >= 0;
          }),
          startWith(''),
          takeUntil(this.unsubscribe),
          distinctUntilChanged(),
          debounceTime(300),
          tap(() => {
            this.errorMsg = undefined;
            this._categories$.next([]);
            this.$isLoading.set(true);
          }),
          switchMap((value) => {
            if (options.categoriesSource === undefined) {
              throw new Error('What is this?');
            }
            return options
              .categoriesSource(this.easControl, this.easControl.control.value, this.easControl.root?.control?.value, value)
              .pipe(
                catchError((er, _caught) => {
                  console.log(er);
                  this.errorMsg = er;
                  return of([]);
                })
              );
          }),
          map((categories) => {
            this.$isLoading.set(false);
            this.initialValueLoaded.set(true);
            this._categories$.next(categories);
            return categories;
            // const name = this.displayFn()(value);
            // const sense = name || value;
            // return this._filter(sense);
          })
        )
        .subscribe();

      const categoriesDependsOn = options.categoriesDependsOn || [];
      const root = this.easControl.root as EasFormGroup;
      // Clear if dependent value changes
      categoriesDependsOn.forEach((fieldId) => {
        const elem = root.getControl(fieldId);
        if (elem) {
          elem.control.valueChanges.subscribe((_) => {
            this.easControl.setValue(null);
          });
        }
      });
    };
    if (this.easControl.control.value && options.getInitial) {
      options
        .getInitial(this.easControl.control.value)
        .pipe(
          map((data) => {
            this.selectedCategories.set(data);
            this.initialValueLoaded.set(true);
            this._categories$.next(data);
            // if (data.length > 0) {
            //   this.easControl.setValue(data[0]['id']);
            // }
            setup();
          })
        )
        .subscribe();
    } else {
      setup();
    }
  }

  private setupCategories() {
    const options = this.easControl.field.options || ({} as EasFieldOptions);
    if (options.categories) {
      this.setupSimpleCategories();
    } else if (options.categoriesFactory) {
      this.setupFactoryCategories();
    } else if (options.categoriesSource !== undefined) {
      this.setupSourceCategories();
    }
  }

  ngAfterViewInit() {
    console.log('input after', this.input);
    this.setupCategories();
  }

  ngOnInit() {
    this.easControl.control.statusChanges.subscribe(() => {
      this.handleErrors()
    });
  }

  displayFn() {
    return (value: string) => {
      if (!this._categories$) {
        return;
      }
      const idField = (this.easControl.field.options && this.easControl.field.options.key) || 'id';
      const opt = this._categories$.value.find((itm) => itm[idField] === value);
      console.log(opt);
      return opt !== undefined ? opt.name : '';
    };
  }

  private _filter(name: string): any[] {
    const ret = this.allCategories;
    if (!name) {
      return ret.slice();
    }

    const filterValue = name.toLowerCase();

    return ret.filter((option) => option.name.toLowerCase().indexOf(filterValue) !== -1);
  }

  remove(category: any): void {
    console.log('Removing', category);
    this.selectedCategories.update(categories => {
      const index = categories.indexOf(category);
      if (index < 0) {
        return categories;
      }

      categories.splice(index, 1);
      return [...categories];
    });
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.selectedCategories.update(categories => [...categories, event.option.value]);
    event.option.deselect();
    this.input.nativeElement.value = '';
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
